import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { ApiConstants } from 'src/app/base/api-constants';
import { Store } from '@ngrx/store';
import { getUser } from 'src/app/STORE/actions/auth.actions';
import { userData } from 'src/app/STORE/selectors/auth.selector';
import { filter } from 'rxjs';
import { HeaderService } from 'src/app/services/header.service';
import { NavigationEnd } from '@angular/router';
import {
  getOrglistforHeader,
  getOrgDataForHeader,
  getSidBasedOrManagement
} from 'src/app/STORE/actions/orgmanagement.action';
import { orglistForHeader, orgDataForHeader } from 'src/app/STORE/selectors/orgmanagement.selector';
import { UtilService } from 'src/app/services/util.service';
import { setAllApps, setApps } from 'src/app/STORE/actions/applifecycle.actions';
import { getUsers, setUsers, getUsersForSearch } from 'src/app/STORE/actions/users.actions';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'vb-header-sidebar',
  templateUrl: './header-sidebar.component.html',
  styleUrls: ['./header-sidebar.component.scss'],

  standalone: false
})
export class HeaderSidebarComponent implements OnInit, OnDestroy {
  public avatarImage: any;
  public loggedInUser: string = '';
  public userEmail: string = '';
  public userMenuItems: any[] = [];
  public raiModeEnabled: boolean = false;

  public isCollapsed: boolean = false;
  public activeMenu: string = 'home';

  public learnUrl: string = environment.learnUrl;
  public documentationUrl: string = environment.documentationUrl;

  public recentApps: any[] = [];

  public orgs: any = [];
  public userOrgs: any = [];
  public selectedOrg: any;
  userRole: any;
  public searchTextOrg: string = '';
  public headerLoader: boolean = false;
  public headerEmpMsg: boolean = false;
  private orgListForHeader$: any;
  private routerOrgId: any = '';
  private user: any = null;
  private readonly subscriptions: any[] = [];

  private router = inject(Router);
  private translateService = inject(TranslateService);
  private appGlobalService = inject(AppGlobalService);
  private httpBaseService = inject(HttpBaseService);
  private store = inject(Store);
  private headerService = inject(HeaderService);
  private utilService = inject(UtilService);
  private activatedRoute = inject(ActivatedRoute);

  menuJSON: any = [];
  selectedApp: any = {};
  maintenanceLabel = 'START_MAINTENANCE';

  ngOnInit(): void {
    this.initializeRouterOrgId();
    this.getCurrentUser();

    // Subscribe to get details of the currently selected organization
    const orgSub = this.store.select(orgDataForHeader).subscribe((orgDataForHeaderRes: any) => {
      if (orgDataForHeaderRes && !orgDataForHeaderRes?.error) {
        this.selectedOrg = orgDataForHeaderRes;
        if (orgSub) {
          orgSub.unsubscribe();
        }
      }
    });
    this.subscriptions.push(orgSub);

    this.loadRecentApps();
    this.initRouteListener();
    this.getOrgList();
  }

  initRouteListener(): void {
    // Initial check on load
    this.updateActiveMenuBasedOnUrl(this.router.url);

    // Listen sequentially for navigation updates
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
      this.updateActiveMenuBasedOnUrl(event.urlAfterRedirects);
    });
  }

  updateActiveMenuBasedOnUrl(url: string): void {
    if (url.includes('manageusers')) {
      this.activeMenu = 'manageusers';
    } else if (url.includes('manageorgs')) {
      this.activeMenu = 'manageorgs';
    } else if (url.includes('clouds-centre')) {
      this.activeMenu = 'clouds-centre';
    } else if (url.includes('/settings/agents')) {
      this.activeMenu = 'agents';
    } else if (url.includes('billing-home')) {
      this.activeMenu = 'subscription';
    } else if (url.includes('prototyping')) {
      this.activeMenu = 'recent';
    } else if (url.includes('appmanagement')) {
      this.activeMenu = 'home';
    }
  }

  getCurrentUser() {
    this.store.dispatch(getUser({ authApiConstants: ApiConstants.authenticateUser }));
    const userSub = this.store
      .select(userData)
      .pipe(
        filter((userInfo: any) => !!userInfo) // Only proceed if userInfo exists
      )
      .subscribe((userInfo: any) => {
        this.loggedInUser = `${userInfo.firstName.charAt(0).toUpperCase() + userInfo.firstName.slice(1)} ${userInfo.lastName.charAt(0).toUpperCase() + userInfo.lastName.slice(1)}`;
        this.userEmail = userInfo.email;
        this.avatarImage = this.getAvatar(userInfo.firstName);
        this.raiModeEnabled = userInfo.raiModeEnabled ?? false;

        this.user = userInfo;
        this.updateUserKebabMenu();
      });
  }

  loadRecentApps(): void {
    const orgId = this.appGlobalService.get('orgId');
    const recentAppsKey = `rappit_recent_apps_${orgId}`;
    try {
      const stored = localStorage.getItem(recentAppsKey);
      if (stored) {
        this.recentApps = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to parse recent apps from local storage', e);
    }
  }

  navigateToHome(): void {
    this.activeMenu = 'home';
    const orgId = this.appGlobalService.get('orgId');
    if (orgId) {
      this.router.navigate(['/home', orgId, 'appId', 'settings', 'appmanagement']);
    }
  }

  navigateToAgents() {
    this.activeMenu = 'agents';
    const orgId = this.appGlobalService.get('orgId');
    if (orgId) {
      this.router.navigate(['/home', orgId, 'appId', 'settings', 'agents']);
    }
  }

  navigateToApp(appId: string): void {
    this.activeMenu = 'recent';
    const orgId = this.appGlobalService.get('orgId');
    if (orgId && appId) {
      this.router.navigate(['/home', orgId, appId, 'prototyping']);
    }
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  getAvatar(name: string) {
    return this.getRandomColor(name);
  }

  getRandomColor(name, s = 50, l = 50) {
    if (!name) {
      return false;
    }
    const firstAlphabet = name?.charAt(0).toLowerCase();

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const h = hash % 360;
    const newColour = `hsl(${h}, ${s}%, ${l}%)`;

    return {
      color: newColour,
      character: firstAlphabet.toUpperCase()
    };
  }

  gotoUsers() {
    const orgId = this.appGlobalService.get('orgId');
    this.router.navigate(['/home', orgId, 'appId', 'settings', 'manageusers']);
  }

  gotoOrganizations() {
    const orgId = this.appGlobalService.get('orgId');
    this.router.navigate(['/home', orgId, 'appId', 'settings', 'manageorgs']);
  }

  gotoCloudsCentre() {
    this.activeMenu = 'clouds-centre';
    const orgId = this.appGlobalService.get('orgId');
    this.router.navigate(['/home', orgId, 'appId', 'dashboards', 'clouds-centre']);
  }

  gotoSubscription() {
    this.activeMenu = 'subscription';
    const orgId = this.appGlobalService.get('orgId');
    this.router.navigate(['/home', orgId, 'appId', 'dashboards', 'billing-home']);
  }

  updateUserKebabMenu() {
    this.userMenuItems = [];

    this.userMenuItems.push({
      label: this.translateService.instant('ORG_LIST'),
      svgIcon: 'assets/designIconLib/SVG/building-07.svg',
      styleClass: 'rai-toggle-menu-item',
      command: () => {
        this.gotoOrganizations();
      }
    });

    this.userMenuItems.push({
      label: this.translateService.instant('MANAGE_USERS'),
      svgIcon: 'assets/designIconLib/SVG/users-01.svg',
      styleClass: 'rai-toggle-menu-item',
      command: () => {
        this.gotoUsers();
      }
    });

    this.userMenuItems.push({
      label: this.translateService.instant('SETTINGS'),
      svgIcon: 'assets/designIconLib/SVG/tool-02.svg',
      styleClass: 'rai-toggle-menu-item',
      command: () => {
        const orgId = this.appGlobalService.get('orgId');
        this.router.navigate(['/home', orgId, 'appId', 'settings', 'environment-availability']);
      }
    });

    this.userMenuItems.push({ separator: true });

    this.userMenuItems.push({
      label: this.translateService.instant('BUILT_WITH_VIBE_MODE'),
      icon: this.raiModeEnabled ? 'fa fa-toggle-on text-primary' : 'fa fa-toggle-off',
      styleClass: 'rai-toggle-menu-item',
      command: () => {
        this.toggleRaiMode();
      }
    });

    this.userMenuItems.push({ separator: true });

    this.userMenuItems.push({
      label: this.translateService.instant('LOGOUT'),
      icon: 'pi pi-sign-out',
      styleClass: 'logout-menu-item',
      command: () => {
        this.triggerLogout();
      }
    });
  }

  toggleRaiMode() {
    this.raiModeEnabled = !this.raiModeEnabled;
    this.httpBaseService
      .put(ApiConstants.toggleRaiModeForCurrentUser, {
        raiModeEnabled: this.raiModeEnabled
      })
      .subscribe({
        next: () => {
          window.location.reload();
        },
        error: (err) => {
          this.raiModeEnabled = !this.raiModeEnabled;
        }
      });
  }

  triggerLogout() {
    this.headerService.triggerLogout();
  }

  ngOnDestroy(): void {
    console.log('ng on destroy');
  }

  getOrgList() {
    if (this.orgListForHeader$) {
      this.orgListForHeader$.unsubscribe();
    }

    this.store.dispatch(
      getOrglistforHeader({
        apiConst: ApiConstants.getOrganizationData,
        searchText: '',
        searchQuery: {
          start: 0,
          length: 100
        }
      })
    );

    this.orgListForHeader$ = this.store.select(orglistForHeader).subscribe((data: any) => {
      if (!data) return;

      if (data.length === 0) {
        this.headerLoader = false;
        this.headerEmpMsg = true;
      } else {
        this.headerEmpMsg = false;
        this.headerLoader = false;

        const orgIds = this.user?.orgIds || [];
        if (this.user?.devAdmin || this.user?.sysAdmin || this.user?.devViewer) {
          this.orgs = data;
        } else {
          this.orgs = data.filter((org: any) => orgIds.includes(org.sid));
        }

        if (!this.searchTextOrg) {
          this.userOrgs = [...this.orgs];
        }

        const currentOrgId =
          this.appGlobalService.get('invalidUrl') === 'true'
            ? this.appGlobalService.get('orgId')
            : this.routerOrgId || this.appGlobalService.get('orgId');

        let org: any = this.orgs.find((e: any) => e.sid === this.selectedOrg?.sid);
        if (!org && this.selectedOrg?.sid) {
          this.orgs = [...this.orgs, this.selectedOrg];
          this.userOrgs = [...this.orgs];
        }

        if (!this.selectedOrg && currentOrgId) {
          this.selectedOrg = this.orgs.find((orgn: any) => orgn.sid === currentOrgId);
        }
      }
    });

    this.subscriptions.push(this.orgListForHeader$);
  }

  getDefaultBranches() {
    const userId = this.user?.sid || '';
    const orgId = this.appGlobalService.get('orgId') || '';
    if (!userId || !orgId) {
      console.log('Missing userId or orgId, skipping API call');
      return;
    }
    this.httpBaseService.get(ApiConstants.getDefaultBranches, { userId, orgId }).subscribe({
      next: (response: any) => {
        if (response?.defaultBranches) {
          this.appGlobalService.write('defaultBranches', response.defaultBranches);
          console.log('Default branches set successfully in Global Service:', response.defaultBranches);
        } else {
          this.appGlobalService.write('defaultBranches', {});
        }
      },
      error: (error: any) => {
        this.appGlobalService.write('defaultBranches', {});
      }
    });
  }

  getUsersForSearch() {
    this.store.dispatch(
      getUsersForSearch({
        apiConstants: ApiConstants.getUsersForSearch,
        filter: {
          start: 0,
          length: 100
        },
        orgChange: true
      })
    );
  }

  handleOrgSelectionChange(event: any) {
    this.searchTextOrg = '';
    this.getOrgList();

    this.selectedOrg = event.value;

    // Save to globals & reload so route resolves to new org context everywhere reliably (just like the header does)
    this.appGlobalService.write('orgId', this.selectedOrg?.sid);
    this.appGlobalService.write('clearSearch', true);

    // Fetch organization-specific data and update UI
    this.store.dispatch(
      getOrgDataForHeader({
        orgApi: ApiConstants.getOrganizationById,
        sid: this.selectedOrg?.sid
      })
    );

    const currentUrl = this.router.url;
    const urlArray = currentUrl.split('/');
    this.store.dispatch(setApps({ appList: [] }));
    this.store.dispatch(setAllApps({ allAppList: [], totalRecords: 0 }));
    this.store.dispatch(setUsers({ usersData: [], totalRecords: 0 }));
    this.getUserRole();
    this.getDefaultBranches();

    if (this.userRole && this.userRole !== 'Viewer' && this.userRole !== 'Prototype Viewer') {
      this.getUsersForSearch();
    }
    this.selectedApp = {};
    if (this.router.url.includes('manageusers') || this.router.url.includes('getOrgDetail')) {
      if (
        !(
          this.userRole === 'sysAdmin' ||
          this.userRole === 'devAdmin' ||
          this.userRole === 'devViewer' ||
          this.userRole === 'Organization Admin'
        )
      ) {
        this.router
          .navigate(['/home', this.selectedOrg?.sid, 'appId', 'settings', 'appmanagement'])
          .catch((error) => console.error(error));
      } else if (this.router.url.includes('manageusers')) {
        this.router
          .navigate(['/home', this.selectedOrg?.sid, 'appId', urlArray[4], urlArray[5]], {
            relativeTo: this.activatedRoute
          })
          .catch((error) => console.error(error));
        this.getUsersForOrganization();
      } else {
        this.router
          .navigate(['/home', this.selectedOrg?.sid, 'appId', urlArray[4], urlArray[5]], {
            relativeTo: this.activatedRoute
          })
          .catch((error) => console.error(error));
        this.getOrgById();
      }
    } else {
      this.router
        .navigate(['/home', this.selectedOrg?.sid, 'appId', urlArray[4], urlArray[5]], {
          relativeTo: this.activatedRoute
        })
        .catch((error) => console.error(error));
    }
  }

  getOrgById() {
    this.store.dispatch(
      getSidBasedOrManagement({
        apiConstants: ApiConstants.getOrganizationById,
        sid: this.selectedOrg?.sid
      })
    );
  }

  searchOrg(event: any) {
    this.searchTextOrg = event.filter;
    this.store.dispatch(
      getOrglistforHeader({
        apiConst: ApiConstants.getOrganizationData,
        searchText: this.searchTextOrg,
        searchQuery: {
          start: 0,
          length: 100
        }
      })
    );
  }

  onDropdownShow() {
    this.searchTextOrg = '';
    this.headerLoader = true;
    this.headerEmpMsg = false;

    this.store.dispatch(
      getOrglistforHeader({
        apiConst: ApiConstants.getOrganizationData,
        searchText: '',
        searchQuery: { start: 0, length: 100 }
      })
    );
  }

  orgDropdownHidden() {
    this.headerLoader = false;
  }

  getUsersForOrganization() {
    this.store.dispatch(
      getUsers({
        apiConstants: ApiConstants.getUsers,
        filter: {
          start: 0,
          length: 100
        },
        orgChange: true
      })
    );
  }

  private createMenuItem(page: string, icon: string, label: string) {
    return {
      routerLink: `/home/${this.routerOrgId}/appId/settings/${page}`,
      src: `assets\\images\\svg\\${icon}`,
      label
    };
  }

  private getOrgAdminMenu() {
    return [
      this.createMenuItem('getOrgDetail', 'Icon _ Dropdown _ List.svg', 'VIEW_ORG'),
      this.createMenuItem('manageusers', 'Icon _ Dropdown _ User.svg', 'USER_MANAGEMENT'),
      this.createMenuItem('appmanagement', 'Icon _ Dropdown _ Apps.svg', 'APP_MANAGEMENT')
    ];
  }

  private getAppAndOrgListMenu() {
    return [
      this.createMenuItem('appmanagement', 'Icon _ Dropdown _ Apps.svg', 'APP_MANAGEMENT'),
      this.createMenuItem('manageorgs', 'Icon _ Dropdown _ List.svg', 'ORG_LIST')
    ];
  }

  private assignMenuForOrgRole(orgRole: string): boolean {
    const rolesWithAppAndOrgList = ['Developer', 'Consultant', 'Viewer', 'Architect'];

    if (orgRole === 'Organization Admin') {
      this.userRole = 'Organization Admin';
      this.menuJSON = this.getOrgAdminMenu();
      return true;
    }

    if (rolesWithAppAndOrgList.includes(orgRole) && this.user.devViewer) {
      this.menuJSON = this.getAppAndOrgListMenu();
      return true;
    }

    if (orgRole === 'Prototype Viewer' && this.user.devViewer) {
      // Prototype Viewer with devViewer - no menu assigned, handled by fallback
      return true;
    }

    return false;
  }

  private handleOrgLevelRole() {
    const orgId = this.appGlobalService.get('orgId');
    const orgRole = this.user.orgInfos?.[orgId];
    this.userRole = orgRole;

    const roleHandled = this.assignMenuForOrgRole(orgRole);

    // Fallback to devViewer if user has devViewer flag but no specific org role was handled
    if (!roleHandled && this.user.devViewer) {
      this.userRole = 'devViewer';
      this.menuJSON = this.getAppAndOrgListMenu();
    }

    // Default fallback menu
    if (!this.menuJSON?.length) {
      this.menuJSON = [this.createMenuItem('appmanagement', 'Icon _ Dropdown _ Apps.svg', 'APP_MANAGEMENT')];
    }
  }

  private handleSystemAdminRole() {
    this.menuJSON = [
      this.createMenuItem('manageorgs', 'Icon _ Dropdown _ List.svg', 'ORG_LIST'),
      this.createMenuItem('manageusers', 'Icon _ Dropdown _ User.svg', 'USER_MANAGEMENT'),
      this.createMenuItem('appmanagement', 'Icon _ Dropdown _ Apps.svg', 'APP_MANAGEMENT')
    ];

    if (this.user.devAdmin) {
      this.userRole = 'devAdmin';
      this.menuJSON.push({
        src: 'assets\\images\\svg\\Icon _ Dropdown _ Apps.svg',
        label: this.maintenanceLabel
      });
    } else {
      this.userRole = 'sysAdmin';
    }

    this.updateUserKebabMenu();
  }

  getUserRole() {
    // Reset menu before reconstructing based on new org/role
    this.menuJSON = [];
    this.initializeRouterOrgId();

    // Handle system-level admin roles first
    if (this.user.devAdmin || this.user.sysAdmin) {
      this.handleSystemAdminRole();
      return;
    }

    // Handle organization-level roles
    this.handleOrgLevelRole();
  }

  private initializeRouterOrgId() {
    if (this.selectedOrg?.sid) {
      this.routerOrgId = this.selectedOrg.sid;
    } else {
      this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
        this.routerOrgId = params.get('orgId');
      });
      // Fallback: extract from URL if paramMap is empty
      if (!this.routerOrgId) {
        const urlArray = this.router.url.split('/');
        if (urlArray.length > 2 && urlArray[1] === 'home') {
          this.routerOrgId = urlArray[2];
        }
      }
    }
  }
}
